import './App.css';
import React, { useState, useCallback, useRef } from 'react';
import ProductGrid from './components/ProductGrid';
import BatchResults from './components/BatchResults';
import RecentSearches from './components/RecentSearches';
import FavoriteStores from './components/FavoriteStores';
import Settings from './components/Settings';
import InfoCard from './components/InfoCard';
import { linkService } from './services/linkService';
import { batchLinkService } from './services/batchLinkService';
import { StoreHistoryProvider, useStoreHistory } from './context/StoreHistoryContext';
import useTheme from './hooks/useTheme';
import ProgressBar from './components/ProgressBar';
import AboutContent from './components/AboutContent';
import HowItWorksContent from './components/HowItWorksContent';
import { analyticsService } from './services/analyticsService';
import { importBatchData } from './services/importService';

const AppContent = () => {
  const { theme } = useTheme();
  const { addToHistory } = useStoreHistory();
  const [url, setUrl] = useState('');
  const [processedData, setProcessedData] = useState(null);
  const [batchData, setBatchData] = useState(null);
  const [linkLoading, setLinkLoading] = useState(false);
  const [batchLoading, setBatchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  const [recentSearches] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentStore, setCurrentStore] = useState('');
  const [totalStores, setTotalStores] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isStopped, setIsStopped] = useState(false);
  const [processingState, setProcessingState] = useState('idle');
  const isPausedRef = useRef(false);
  const isStoppedRef = useRef(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);

  // Reset functions
  const resetFileInput = useCallback(() => {
    setSelectedFile(null);
    const fileInput = document.getElementById('file-upload');
    if (fileInput) fileInput.value = '';
  }, []);

  const resetAppState = useCallback(() => {
    setUrl('');
    setProcessedData(null);
    setBatchData(null);
    setError(null);
    setSelectedFile(null);
    setLinkLoading(false);
    setBatchLoading(false);
    setShowSettings(false);
    setShowAbout(false);
    setShowHowItWorks(false);
    // Reset progress bar states
    setUploadProgress(0);
    setCurrentStore('');
    setTotalStores(0);
    setIsStopped(false);
    isStoppedRef.current = false;
    setIsPaused(false);
    isPausedRef.current = false;
    setProcessingState('idle');
    resetFileInput();
  }, [resetFileInput]);

  // Event handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLinkLoading(true);
    setError(null);
    setBatchData(null);
    setProcessedData(null);
    const startTime = Date.now();

    try {
      const result = await linkService.processLink(url);
      if (result.message) {
        setError(result.message);
      }
      setProcessedData(result);
      
      if (result.success) {
        addToHistory(result.processedUrl, result.freeProducts?.length || 0);
      }

      // Record analytics for single search
      analyticsService.recordSearch(
        result.processedUrl,
        result.success,
        result.freeProducts?.length || 0,
        Date.now() - startTime,
        result.message || null
      );
    } catch (err) {
      setError(err.message);
      setProcessedData(null);
      // Record failed search analytics
      analyticsService.recordSearch(
        url,
        false,
        0,
        Date.now() - startTime,
        err.message
      );
    } finally {
      setLinkLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/plain') {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      alert('Please select a .txt file');
      e.target.value = '';
    }
  };

  const handleFileSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;

    setBatchLoading(true);
    setError(null);
    setProcessedData(null);
    setBatchData(null);
    setUploadProgress(0);
    setCurrentStore('');
    isPausedRef.current = false;
    isStoppedRef.current = false;
    setIsPaused(false);
    setIsStopped(false);
    setProcessingState('processing');
    const startTime = Date.now();

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const content = event.target.result;
          const uniqueUrls = new Set();
          const links = content.split('\n')
            .map(line => line.trim())
            .filter(line => line)
            .filter(url => {
              try {
                const normalizedUrl = new URL(url.startsWith('http') ? url : `https://${url}`)
                  .hostname.replace(/^www\./, '');
                if (uniqueUrls.has(normalizedUrl)) {
                  return false;
                }
                uniqueUrls.add(normalizedUrl);
                return true;
              } catch {
                return true;
              }
            });

          // Set total stores before processing
          setTotalStores(links.length);

          const result = await batchLinkService.processBatchLinks(
            links.join('\n'),
            {
              isPausedRef,
              isStoppedRef,
              setUploadProgress,
              setCurrentStore,
              setProcessingState
            }
          );

          setBatchData(result);
          
          // Only set these if we completed normally (not stopped)
          if (!isStoppedRef.current) {
            setUploadProgress(100);
            setCurrentStore('Complete');
            setIsStopped(true);
          }

          // Send analytics for completed batch
          analyticsService.recordBatchSearch(
            result,
            Date.now() - startTime,
            isStoppedRef.current
          );
        } catch (error) {
          setError(error.message);
          console.error('Processing error:', error);
        } finally {
          // If we're in finishing state, wait for the state to change to idle
          if (processingState === 'finishing') {
            const checkState = () => {
              if (processingState === 'idle') {
                setBatchLoading(false);
                setIsStopped(true);
              } else {
                setTimeout(checkState, 100);
              }
            };
            checkState();
          } else {
            setBatchLoading(false);
          }
        }
      };

      reader.readAsText(selectedFile);
    } catch (error) {
      setError('Failed to read the file. Please try again.');
      setBatchLoading(false);
      setProcessingState('idle');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!linkLoading && !batchLoading) {
      setIsDragging(true);
    }
  }, [linkLoading, batchLoading]);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    if (linkLoading || batchLoading) return;

    const file = e.dataTransfer.files[0];
    if (file && file.type === 'text/plain') {
      setSelectedFile(file);
    } else {
      setSelectedFile(null);
      alert('Please select a .txt file');
    }
  }, [linkLoading, batchLoading]);

  const handleStoreSelect = useCallback(async (storeUrl) => {
    setUrl(storeUrl);
    setLinkLoading(true);
    setError(null);
    setBatchData(null);

    try {
      const result = await linkService.processLink(storeUrl);
      if (result.message) {
        setError(result.message);
      }
      setProcessedData(result);
      
      if (result.success) {
        addToHistory(result.processedUrl, result.freeProducts?.length || 0);
      }
    } catch (err) {
      setError(err.message);
      setProcessedData(null);
    } finally {
      setLinkLoading(false);
    }
  }, [addToHistory]);

  const handleImportResults = useCallback((importedData) => {
    setBatchData(importedData);
    setProcessedData(null);
    setError(null);
  }, []);

  const handlePauseResume = useCallback(() => {
    isPausedRef.current = !isPausedRef.current;
    setIsPaused(isPausedRef.current);
  }, []);

  const handleStop = useCallback(() => {
    isStoppedRef.current = true;
    setIsStopped(true);
    setProcessingState('finishing');
    setUrl('');
    resetFileInput();
  }, [resetFileInput]);

  const showResults = processedData || batchData;

  return (
    <div className={`App ${theme}`}>
      <header className="App-header" onClick={resetAppState} role="button" tabIndex={0}>
        <h1>
          Free Finder
          <i className="fas fa-search"></i>
        </h1>
        <div className="header-buttons">
          <button 
            className="header-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowAbout(true);
              setShowSettings(false);
              setShowHowItWorks(false);
            }}
          >
            <i className="fas fa-info"></i>
            <span>About</span>
          </button>
          <button 
            className="header-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowHowItWorks(true);
              setShowSettings(false);
              setShowAbout(false);
            }}
          >
            <i className="fas fa-question"></i>
            <span>How It Works</span>
          </button>
          <button 
            className="header-button"
            onClick={(e) => {
              e.stopPropagation();
              setShowSettings(true);
              setShowAbout(false);
              setShowHowItWorks(false);
            }}
          >
            <i className="fas fa-cog"></i>
            <span>Settings</span>
          </button>
        </div>
      </header>
      
      <main className="App-main">
        <div className="input-container">
          <div className="input-section">
            <div className="section-header">
              <h2>Link Input</h2>
            </div>
            <form onSubmit={handleSubmit} className="input-form">
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="Enter your link here..."
                className="text-input"
                disabled={linkLoading || batchLoading}
              />
              <button 
                type="submit" 
                className="submit-button" 
                disabled={linkLoading || batchLoading || !url}
              >
                {linkLoading ? 'Processing...' : 'Find'}
              </button>
            </form>
            {error && <div className="error-message">{error}</div>}
          </div>

          <div className="input-section">
            <div className="section-header">
              <h2>File Upload</h2>
              <div style={{ width: '8rem', flexShrink: 0 }}>
                <label htmlFor="import-results" className="import-button">
                  {isImporting ? (
                    <>
                      <i className="fas fa-spinner fa-spin"></i>
                      {` ${Math.round(importProgress)}%`}
                    </>
                  ) : (
                    <>
                      <i className="fas fa-download"></i> Import
                    </>
                  )}
                </label>
              </div>
              <input
                id="import-results"
                type="file"
                accept="text/csv,.csv"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setIsImporting(true);
                    setImportProgress(0);
                    try {
                      const importedData = await importBatchData(file, (progress) => {
                        setImportProgress(progress);
                      });
                      handleImportResults(importedData);
                    } catch (error) {
                      alert(error.message);
                    } finally {
                      setIsImporting(false);
                      setImportProgress(0);
                    }
                  }
                  e.target.value = '';
                }}
                style={{ display: 'none' }}
                disabled={isImporting}
              />
            </div>
            <form onSubmit={handleFileSubmit} className="input-form">
              <div 
                className={`file-input-container ${isDragging ? 'dragging' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  accept=".txt"
                  onChange={handleFileChange}
                  className="file-input"
                  id="file-upload"
                  disabled={linkLoading || batchLoading}
                />
                <label htmlFor="file-upload" className="file-label">
                  {selectedFile ? selectedFile.name : 'Choose a .txt file or drag it here'}
                </label>
              </div>
              <button 
                type="submit" 
                className="submit-button" 
                disabled={!selectedFile || linkLoading || batchLoading}
              >
                {batchLoading ? 'Processing...' : 'Upload'}
              </button>
              {batchLoading && (
                <div className="batch-controls">
                  <button 
                    type="button" 
                    className={`control-button pause-button ${isPaused ? 'resume' : ''}`}
                    onClick={handlePauseResume}
                    disabled={isStopped}
                    title={isPaused ? 'Resume processing' : 'Pause processing'}
                  >
                    <i className={`fas fa-${isPaused ? 'play' : 'pause'}`}></i>
                  </button>
                  <button 
                    type="button" 
                    className="control-button stop-button"
                    onClick={handleStop}
                    disabled={isStopped}
                    title="Stop processing"
                  >
                    <i className="fas fa-stop"></i>
                  </button>
                </div>
              )}
            </form>
            <ProgressBar 
              isVisible={batchLoading || isStopped} 
              progress={uploadProgress}
              currentStore={currentStore}
              totalStores={totalStores}
              isPaused={isPaused}
              isStopped={isStopped}
              processingState={processingState}
            />
          </div>
        </div>

        {!showResults && <RecentSearches searches={recentSearches} onStoreSelect={handleStoreSelect} />}
        {!showResults && <FavoriteStores onStoreSelect={handleStoreSelect} />}

        {!showResults && (
          <div className="info-section">
            <InfoCard
              icon="link"
              title="Single Link Search"
              description="Enter a Shopify store URL to find free products"
              example="e.g., madeincookware.com"
            />
            <InfoCard
              icon="file-alt"
              title="Batch Processing"
              description="Upload a .txt file with one URL per line"
              example="Multiple stores processed at once"
            />
            <InfoCard
              icon="shopping-cart"
              title="Free Products"
              description="Automatically finds items priced at $0.00"
              example="Shipping costs may still apply"
            />
          </div>
        )}

        {(processedData || batchData) && (
          <div className="results-section">
            {processedData && (
              <ProductGrid
                products={processedData.freeProducts}
                allProducts={processedData.allProducts}
                isValidStore={processedData.isShopifyStore}
              />
            )}
            {batchData && (
              <BatchResults 
                batchData={batchData}
                onImportResults={handleImportResults}
              />
            )}
          </div>
        )}

        {/* CreatorCard and DisclaimerCard hidden */}

        {showAbout && <AboutContent onClose={() => setShowAbout(false)} />}
        {showHowItWorks && <HowItWorksContent onClose={() => setShowHowItWorks(false)} />}
        {showSettings && <Settings onClose={() => setShowSettings(false)} />}
      </main>
    </div>
  );
};

function App() {
  return (
    <StoreHistoryProvider>
      <AppContent />
    </StoreHistoryProvider>
  );
}

export default App;